import { destroy, getParent, getSnapshot, types } from "mobx-state-tree";

import { VideoStore as Video } from "./VideoStore";
import { QuizStore as Quiz } from "./QuizStore";
import { ResourceStore as Resource } from "./ResourceStore";
import { ShortcutStore as Shortcut } from "./ShortcutStore";

import { round } from "lodash";

export const LessonStore = types
  .model({
    id: types.identifierNumber,
    name: types.maybeNull(types.string),
    description: types.maybeNull(types.string),

    /**
     * Availabled for free user or not
     */
    status: types.optional(types.enumeration(["private", "public"]), "private"),

    /**
     * Duration of lesson in seconds
     */
    duration: types.maybeNull(types.optional(types.integer, 0)),

    /**
     * Lesson progress in percentages
     */
    progress: types.optional(types.integer, -1),

    /**
     * Lesson content type
     */
    lesson_type: types.maybeNull(types.enumeration(["video", "document", "quiz"])),

    /**
     * Lesson content ID
     * { Video | Document | Quiz } ID
     */
    model_id: types.maybeNull(types.integer),

    /**
     * Lesson Video
     */
    video: types.maybeNull(Video),

    /**
     * Lesson Video
     */
    quiz: types.maybeNull(Quiz),

    /**
     * Lesson resources
     */
    resources: types.array(Resource),

    /**
     * Lesson shortcuts
     */
    keyboard_shortcuts: types.array(Shortcut),
  })
  .views((self) => ({
    get isDone() {
      return self.progress >= 80;
    },

    get app() {
      return getParent(self, 2);
    },

    /**
     * @return {Chapter}
     */
    get chapter() {
      const app = getParent(self, 2);

      return app.chapters.find((chapter) => chapter.lessons.includes(self));
    },

    // Percentage to Seconds
    get progressInSeconds() {
      if (self.video?.duration > 0) return Math.floor((self.video.duration * self.progress) / 100);
      return 0;
    },
  }))
  .actions((self) => {
    const lessonType = ["video", "quiz"];
    const fillable = ["name", "description", "duration", "lesson_type", "model_id"];

    const update = (newData = {}) => {
      fillable.forEach((field) => {
        if (!!newData[field]) {
          self[field] = newData[field];
        }
      });

      lessonType.forEach((type) => {
        if (!!newData[type]) {
          if (!self[type]) {
            self[type] = newData[type];
          } else {
            self[type].update(newData[type]);
          }
        }
      });
    };

    const toggleStatus = () => {
      self.status = self.status === "private" ? "public" : "private";
    };

    const updateProgress = (progress, unit = "percentage") => {
      if (unit === "seconds") {
        self.progress = _.round((progress / self.video.duration) * 100, 0);
      } else {
        self.progress = progress ? round(progress, 0) : 0;
      }
    };

    // RESOURCES

    const addResource = (newResource) => {
      self.resources.push(newResource);
    };

    const updateResource = (id, newData) => {
      let index = self.resources.findIndex((r) => r.id === id);

      if (index >= 0) {
        self.resources[index] = newData;
      }
    };

    const deleteResource = (resourceId) => {
      const resource = self.resources.find((r) => r.id === resourceId);
      resource && destroy(resource);
    };

    // SHORTCUTS

    const addShortcut = (newShortcut) => {
      self.keyboard_shortcuts.push(newShortcut);
    };

    const deleteShortcut = (shortcutId) => {
      const shortcut = self.keyboard_shortcuts.find((s) => s.id === shortcutId);
      shortcut && destroy(shortcut);
    };

    return {
      update,

      toggleStatus,
      updateProgress,

      addResource,
      updateResource,
      deleteResource,

      addShortcut,
      deleteShortcut,
    };
  });
